import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Card,
  Label,
  TextInput,
  Button,
  useThemeMode
} from "flowbite-react";
import { HiSearch, HiDownload } from "react-icons/hi";
import ReactApexChart from "react-apexcharts";
import { toast } from "@/lib/toast";
import { ApexOptions } from "apexcharts";
import PaginatedSelect from "../../components/PaginatedSelect";
import DateRangePresets from "../../components/reports/DateRangePresets";

import { 
  useLazyGetTicketAnalysisReportQuery, 
  useDownloadTicketAnalysisReportMutation
} from "../../api/reportsApi/reports";

import {
  useGetStatusesQuery,
  useGetCategoriesQuery,
} from "../../api/ticketApi/selectInputApis";
import { useGetAgentsQuery } from "../../api/agentApi/agent";
import { useGetClientGroupsQuery } from "../../api/clientGroupApi/clientGroup";
import { getErrorMessage } from "../../utils/requestUtils";
import { useReportCompanyParam } from "../../hooks/useReportCompanyParam";

const TicketAnalysisReport: FC = () => {
  const { t } = useTranslation(["reports"]);
  const { mode } = useThemeMode();
  const isDark = mode === "dark";

  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  // States for dropdown selections
  const [selectedAgent, setSelectedAgent] = useState<{label: string, value: string} | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<{label: string, value: string} | null>(null);
  const [selectedClientGroup, setSelectedClientGroup] = useState<{label: string, value: string} | null>(null);
  // Category filter: empty = "All Categories" (within what the user may see).
  const [selectedCategories, setSelectedCategories] = useState<{label: string, value: string}[]>([]);

  // Pagination states
  const [agentsPage, setAgentsPage] = useState(1);
  const [agentsInput, setAgentsInput] = useState("");

  const [clientGroupsPage, setClientGroupsPage] = useState(1);
  const [clientGroupsInput, setClientGroupsInput] = useState("");

  const [triggerReport, { data: reportData, isLoading }] = useLazyGetTicketAnalysisReportQuery();
  const [downloadReport, { isLoading: isDownloading }] = useDownloadTicketAnalysisReportMutation();
  const companyParam = useReportCompanyParam();

  const { data: agentsData, isFetching: isAgentsLoading } = useGetAgentsQuery({
    page: agentsPage, 
    pageSize: 15,
    agent_username: agentsInput 
  });
  
  const { data: statusesData, isFetching: isStatusesLoading } = useGetStatusesQuery();

  // Fetch all categories in one shot (large pageSize) so the multi-select shows
  // every category — the /gw/categories endpoint otherwise paginates to 10.
  const { data: categoriesData, isFetching: isCategoriesLoading } = useGetCategoriesQuery({
    page: 1,
    pageSize: 1000,
  });
  
  const { data: clientGroupsData, isFetching: isClientGroupsLoading } = useGetClientGroupsQuery({
    pagination: { page: clientGroupsPage, pageSize: 15 },
    filters: { group_name: clientGroupsInput }
  });

  // Transform data for dropdowns
  const agentOptions = agentsData?.items?.map((agent: any) => ({
      label: agent.display_name || agent.agent_username,
      value: String(agent.agent_id),
  })) || [];

  const statusOptions = statusesData?.items?.map((status: any) => ({
      label: status.status_name,
      value: String(status.status_id),
  })) || [];

  const categoriesList = categoriesData?.items ?? categoriesData?.data ?? categoriesData ?? [];
  const categoryOptions = (Array.isArray(categoriesList) ? categoriesList : []).map((category: any) => ({
      label: category.category_name,
      value: String(category.category_id),
  }));

  const clientGroupOptions = clientGroupsData?.items?.map((group: any) => ({
      label: group.group_name,
      value: String(group.client_group_id),
  })) || [];

  // Helper functions to aggregate data
  const getTicketsByStatus = (tickets: any[]) => {
    const counts: Record<string, number> = {};
    tickets.forEach(ticket => {
      const status = ticket.status || 'Unknown';
      counts[status] = (counts[status] || 0) + 1;
    });
    return Object.entries(counts).map(([status_name, count]) => ({ status_name, count }));
  };

  const getTicketsByAgent = (tickets: any[]) => {
    const counts: Record<string, number> = {};
    tickets.forEach(ticket => {
      const agent = ticket.agent || 'Unassigned';
      counts[agent] = (counts[agent] || 0) + 1;
    });
    return Object.entries(counts).map(([agent_name, count]) => ({ agent_name, count }));
  };

  const getTicketsOverTime = (tickets: any[]) => {
    const counts: Record<string, number> = {};
    tickets.forEach(ticket => {
      if (ticket.created_at) {
        const date = new Date(ticket.created_at).toISOString().split('T')[0];
        counts[date] = (counts[date] || 0) + 1;
      }
    });
    return Object.entries(counts)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([date, count]) => ({ date, count }));
  };

  // Average resolution time = mean(closed_at - created_at) over tickets that
  // have actually been resolved (closed_at is set when a ticket becomes
  // Resolved). Returns { avgMs, resolvedCount }.
  const getResolutionStats = (tickets: any[]) => {
    let sumMs = 0;
    let resolvedCount = 0;
    tickets.forEach((ticket) => {
      if (ticket.closed_at && ticket.created_at) {
        const created = new Date(ticket.created_at).getTime();
        const closed = new Date(ticket.closed_at).getTime();
        if (!isNaN(created) && !isNaN(closed) && closed >= created) {
          sumMs += closed - created;
          resolvedCount += 1;
        }
      }
    });
    return { avgMs: resolvedCount > 0 ? sumMs / resolvedCount : null, resolvedCount };
  };

  // Human-readable duration with full breakdown down to seconds, e.g.
  // "2 gün 5 sa 12 dk 30 sn" / "3 sa 20 dk 5 sn" / "5 dk 12 sn" / "46 sn".
  // Leading zero units are dropped; minutes and seconds are always shown.
  const formatDuration = (ms: number | null): string => {
    if (ms === null) return t("notAvailable");
    const totalSeconds = Math.round(ms / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const parts: string[] = [];
    if (days > 0) parts.push(`${days} ${t("unitDay")}`);
    if (hours > 0 || days > 0) parts.push(`${hours} ${t("unitHour")}`);
    parts.push(`${minutes} ${t("unitMinute")}`);
    parts.push(`${seconds} ${t("unitSecond")}`);
    return parts.join(" ");
  };

  // Memoized aggregations
  const ticketsByStatus = reportData?.report ? getTicketsByStatus(reportData.report) : [];
  const ticketsByAgent = reportData?.report ? getTicketsByAgent(reportData.report) : [];
  const ticketsOverTime = reportData?.report ? getTicketsOverTime(reportData.report) : [];
  const totalTickets = reportData?.meta?.total_tickets || 0;
  const resolutionStats = reportData?.report
    ? getResolutionStats(reportData.report)
    : { avgMs: null, resolvedCount: 0 };

  const handleSearch = async () => {
    if (!startDate || !endDate) {
      toast.warn(t("selectDateWarning"));
      return;
    }

    if (startDate > endDate) {
      toast.error(t("startDateHigherThanEndDate"));
      return;
    }

    try {
      await triggerReport({
        start_date: startDate,
        end_date: endDate,
        agent_id: selectedAgent?.value ? Number(selectedAgent.value) : undefined,
        last_status_id: selectedStatus?.value ? Number(selectedStatus.value) : undefined,
        client_group_id: selectedClientGroup?.value ? Number(selectedClientGroup.value) : undefined,
        category_ids: selectedCategories.length ? selectedCategories.map((c) => c.value).join(",") : undefined,
        ...companyParam
      }).unwrap();
    } catch (error: any) {
       toast.error(getErrorMessage(error));
    }
  };

  const handleDownload = async () => {
    if (!startDate || !endDate) {
        toast.warn(t("selectClientDateWarning"));
        return;
    }

    try {
      const response = await downloadReport({
        start_date: startDate,
        end_date: endDate,
        agent_id: selectedAgent?.value ? Number(selectedAgent.value) : undefined,
        last_status_id: selectedStatus?.value ? Number(selectedStatus.value) : undefined,
        client_group_id: selectedClientGroup?.value ? Number(selectedClientGroup.value) : undefined,
        category_ids: selectedCategories.length ? selectedCategories.map((c) => c.value).join(",") : undefined
      }).unwrap();
      
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ticket-analysis-report-${startDate}-to-${endDate}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      toast.error(getErrorMessage(error));
    }
  };

  const statusChartOptions: ApexOptions = {
    chart: { type: 'pie' },
    labels: ticketsByStatus.map(s => s.status_name),
    theme: { mode: isDark ? 'dark' : 'light' },
    colors: ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6']
  };

  const statusChartSeries = ticketsByStatus.map(s => s.count);

  const agentChartOptions: ApexOptions = {
    chart: { type: 'bar' },
    xaxis: { categories: ticketsByAgent.map(a => a.agent_name) },
    theme: { mode: isDark ? 'dark' : 'light' },
    colors: ['#3B82F6']
  };

  const agentChartSeries = [{
    name: 'Tickets',
    data: ticketsByAgent.map(a => a.count)
  }];

  const timeChartOptions: ApexOptions = {
    chart: { type: 'line' },
    xaxis: { categories: ticketsOverTime.map(t => t.date) },
    theme: { mode: isDark ? 'dark' : 'light' },
    stroke: { curve: 'smooth' },
    colors: ['#10B981']
  };

  const timeChartSeries = [{
    name: 'Tickets',
    data: ticketsOverTime.map(t => t.count)
  }];

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <DateRangePresets
          className="mb-4"
          startDate={startDate}
          endDate={endDate}
          onChange={(s, e) => { setStartDate(s); setEndDate(e); }}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-4 items-end">
            <div>
                <div className="mb-2 block">
                    <Label htmlFor="startDate" value={t("startDate")} />
                </div>
                <TextInput
                    id="startDate"
                    type="date"
                    required
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                />
            </div>
            <div>
                <div className="mb-2 block">
                    <Label htmlFor="endDate" value={t("endDate")} />
                </div>
                <TextInput
                    id="endDate"
                    type="date"
                    required
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                />
            </div>

            <div>
                <PaginatedSelect
                    id="agent"
                    label={t("agent")}
                    value={selectedAgent}
                    onChange={(val) => {
                      if (Array.isArray(val)) return; // Should be single value
                      setSelectedAgent(val as { label: string; value: string } | null);
                    }}
                    options={agentOptions}
                    onInputChange={(val) => { setAgentsInput(val); setAgentsPage(1); }}
                    isLoading={isAgentsLoading}
                    placeholder={t("allAgents")}
                    page={agentsPage}
                    totalPages={agentsData?.pagination?.totalPages || 1}
                    onPageChange={setAgentsPage}
                    menuMaxHeight={350}
                />
            </div>

            <div>
                <PaginatedSelect
                    id="status"
                    label={t("status")}
                    value={selectedStatus}
                    onChange={(val) => {
                       if (Array.isArray(val)) return;
                       setSelectedStatus(val as { label: string; value: string } | null);
                    }}
                    options={statusOptions}
                    onInputChange={() => {}} // No search for status
                    isLoading={isStatusesLoading}
                    placeholder={t("allStatuses")}
                    page={1}
                    totalPages={1}
                    onPageChange={() => {}}
                    menuMaxHeight={350}
                />
            </div>

            <div>
                <PaginatedSelect
                    id="category"
                    isMulti
                    label={t("category")}
                    value={selectedCategories}
                    onChange={(val) => {
                      if (Array.isArray(val)) {
                        setSelectedCategories(val as { label: string; value: string }[]);
                      } else if (val) {
                        setSelectedCategories([val as { label: string; value: string }]);
                      } else {
                        setSelectedCategories([]);
                      }
                    }}
                    options={categoryOptions}
                    onInputChange={() => {}}
                    isLoading={isCategoriesLoading}
                    placeholder={t("allCategories")}
                    page={1}
                    totalPages={1}
                    onPageChange={() => {}}
                    menuMaxHeight={350}
                />
            </div>

            <div>
                <PaginatedSelect
                   id="clientGroup"
                   label={t("clientGroup")}
                   value={selectedClientGroup}
                   onChange={(val) => {
                      if (Array.isArray(val)) return;
                      setSelectedClientGroup(val as { label: string; value: string } | null);
                   }}
                   options={clientGroupOptions}
                   onInputChange={(val) => { setClientGroupsInput(val); setClientGroupsPage(1); }}
                   isLoading={isClientGroupsLoading}
                   placeholder={t("allClientGroups")}
                   page={clientGroupsPage}
                   totalPages={clientGroupsData?.pagination?.totalPages || 1}
                   onPageChange={setClientGroupsPage}
                   menuMaxHeight={350}
                />
            </div>

            <div className="flex gap-2">
                <Button color="primary" onClick={handleSearch} disabled={isLoading} isProcessing={isLoading}>
                    <HiSearch className="mr-2 h-5 w-5" />
                    {t("search")}
                </Button>
                 <Button color="gray" onClick={handleDownload} disabled={isDownloading || !reportData} isProcessing={isDownloading}>
                    <HiDownload className="mr-2 h-5 w-5" />
                    {t("downloadPDF")}
                </Button>
            </div>
        </div>
      </Card>

      {reportData && reportData.report && reportData.report.length === 0 && (
        <div className="flex justify-center items-center p-10 bg-white rounded-lg border border-gray-200 shadow-md dark:bg-gray-800 dark:border-gray-700">
             <div className="text-center">
                <h5 className="mb-2 text-2xl font-bold tracking-tight text-gray-900 dark:text-white">{t("noDataTitle")}</h5>
                <p className="font-normal text-gray-700 dark:text-gray-400">{t("noDataMessage")}</p>
             </div>
        </div>
      )}

      {reportData && reportData.report && reportData.report.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
               <Card>
                  <h5 className="text-xl font-bold tracking-tight text-gray-900 dark:text-white mb-4">
                      {t("ticketsByStatus")}
                  </h5>
                  <ReactApexChart options={statusChartOptions} series={statusChartSeries} type="pie" height={350} />

                  {/* Verimlilik metrikleri — patron isteğiyle bu şemanın içinde */}
                  <div className="mt-4 grid grid-cols-2 gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
                       <div className="flex flex-col items-center rounded-lg bg-gray-50 p-3 dark:bg-gray-700/40">
                            <span className="text-sm text-gray-500 dark:text-gray-400">{t("resolvedTickets")}</span>
                            <span className="text-xl font-bold text-green-600 dark:text-green-400">{resolutionStats.resolvedCount}</span>
                       </div>
                       <div className="flex flex-col items-center rounded-lg bg-gray-50 p-3 dark:bg-gray-700/40">
                            <span className="text-sm text-gray-500 dark:text-gray-400" title={t("avgResolutionTimeHint")}>{t("avgResolutionTime")}</span>
                            <span className="text-xl font-bold text-amber-600 dark:text-amber-400">{formatDuration(resolutionStats.avgMs)}</span>
                       </div>
                  </div>
               </Card>

               <Card>
                  <h5 className="text-xl font-bold tracking-tight text-gray-900 dark:text-white mb-4">
                      {t("ticketsByAgent")}
                  </h5>
                  <ReactApexChart options={agentChartOptions} series={agentChartSeries} type="bar" height={350} />
               </Card>

               <Card className="md:col-span-2">
                  <h5 className="text-xl font-bold tracking-tight text-gray-900 dark:text-white mb-4">
                      {t("ticketsOverTime")}
                  </h5>
                  <ReactApexChart options={timeChartOptions} series={timeChartSeries} type="line" height={350} />
               </Card>
               
               <Card className="md:col-span-2">
                    <div className="flex items-center justify-between">
                         <span className="text-xl font-bold text-gray-900 dark:text-white">{t("totalTickets")}:</span>
                         <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">{totalTickets}</span>
                    </div>
               </Card>
          </div>
      )}
    </div>
  );
};

export default TicketAnalysisReport;
